/**
 * Organization Context Bar (Sprint 9 - DC-447)
 *
 * Compact bar showing organization context summary in journey views.
 * Provides navigation to Organization Settings for configuring:
 * - Interviews (People Readiness)
 * - Documents (Governance Maturity)
 * - Standards (Interoperability)
 *
 * @see Documentation/Development/Design Docs/UX_Architecture_Redesign_v1.0.md Section 7
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './org-context-bar.css';

interface ExecutionRiskData {
  enabled: boolean;
  is_default: boolean;
  combined_factor?: number;
  risk_level?: string;
  people_readiness?: { factor: number };
  governance_maturity?: { factor: number };
  interoperability_maturity?: { factor: number };
}

interface OrgContextData {
  execution_risk: ExecutionRiskData;
  interview_count: number;
  document_count: number;
  standards_enabled: number;
  standards_total: number;
}

interface OrgContextBarProps {
  onOrgSettingsClick?: () => void;
}

export const OrgContextBar: React.FC<OrgContextBarProps> = ({
  onOrgSettingsClick,
}) => {
  const navigate = useNavigate();
  const [data, setData] = useState<OrgContextData | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    fetchOrgContext();
  }, []);

  const fetchOrgContext = async () => {
    try {
      const response = await fetch('/api/organization/execution-risk');
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (err) {
      console.error('[OrgContextBar] Failed to fetch org context:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleNavigateToSettings = () => {
    if (onOrgSettingsClick) {
      onOrgSettingsClick();
    } else {
      // DC-1646: the execution-risk readout's canonical home
      navigate('/readiness/organizational');
    }
  };

  const getRiskLevelColor = (level?: string): string => {
    switch (level) {
      case 'LOW': return 'risk-low';
      case 'MODERATE': return 'risk-moderate';
      case 'HIGH': return 'risk-high';
      case 'CRITICAL': return 'risk-critical';
      default: return 'risk-unknown';
    }
  };

  const formatPercentage = (value?: number): string => {
    if (value === undefined || value === null) return '--';
    return `${Math.round(value * 100)}%`;
  };

  if (loading) {
    return (
      <div className="org-context-bar loading">
        <span className="loading-text">Loading org context...</span>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const { execution_risk, interview_count, document_count, standards_enabled, standards_total } = data;
  const isColdStart = execution_risk.is_default;

  return (
    <div className={`org-context-bar ${expanded ? 'expanded' : ''} ${isColdStart ? 'cold-start' : ''}`}>
      {/* Compact View */}
      <div className="context-bar-compact" onClick={() => setExpanded(!expanded)}>
        <div className="context-summary">
          <span className="context-label">Organization Context:</span>
          {isColdStart ? (
            <span className="cold-start-badge">Not Configured</span>
          ) : (
            <>
              <span className={`risk-indicator ${getRiskLevelColor(execution_risk.risk_level)}`}>
                {execution_risk.risk_level || 'N/A'}
              </span>
              <span className="data-counts">
                {interview_count} interviews | {document_count} docs | {standards_enabled}/{standards_total} standards
              </span>
            </>
          )}
        </div>
        <div className="context-actions">
          <button
            className="settings-link"
            onClick={(e) => {
              e.stopPropagation();
              handleNavigateToSettings();
            }}
          >
            <span className="settings-icon">&#9881;</span>
            Org Settings
          </button>
          <span className="expand-icon">{expanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {/* Expanded Panel */}
      {expanded && (
        <div className="context-bar-expanded">
          <div className="org-factors">
            <div className="factor-card">
              <span className="factor-icon">&#128101;</span>
              <div className="factor-info">
                <span className="factor-label">People Readiness</span>
                <span className="factor-value">
                  {formatPercentage(execution_risk.people_readiness?.factor)}
                </span>
              </div>
              <span className="factor-source">({interview_count} interviews)</span>
            </div>
            <div className="factor-card">
              <span className="factor-icon">&#128196;</span>
              <div className="factor-info">
                <span className="factor-label">Governance Maturity</span>
                <span className="factor-value">
                  {formatPercentage(execution_risk.governance_maturity?.factor)}
                </span>
              </div>
              <span className="factor-source">({document_count} documents)</span>
            </div>
            <div className="factor-card">
              <span className="factor-icon">&#128279;</span>
              <div className="factor-info">
                <span className="factor-label">Interoperability</span>
                <span className="factor-value">
                  {formatPercentage(execution_risk.interoperability_maturity?.factor)}
                </span>
              </div>
              <span className="factor-source">({standards_enabled}/{standards_total} standards)</span>
            </div>
          </div>

          {isColdStart && (
            <div className="cold-start-message">
              <p>
                <strong>Configure your organization context</strong> for more accurate recommendations
                and ROI projections. Add interviews, governance documents, and enable interoperability standards.
              </p>
              <button
                className="configure-btn"
                onClick={handleNavigateToSettings}
              >
                Configure Organization
              </button>
            </div>
          )}

          {!isColdStart && execution_risk.combined_factor !== undefined && (
            <div className="combined-factor">
              <span className="combined-label">Execution Risk Factor:</span>
              <span className={`combined-value ${getRiskLevelColor(execution_risk.risk_level)}`}>
                {formatPercentage(execution_risk.combined_factor)}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OrgContextBar;
